import {
  Building2,
  CheckCircle2,
  Circle,
  ExternalLink,
  GraduationCap,
  History,
  KeyRound,
  PhoneCall,
  PieChart,
  ShieldAlert,
  UserRound,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { fmtDate, fmtMoney } from '@/features/shared/format'
import { cn } from '@/lib/utils'
import { KvRow, SectionCard } from '../components/SectionCard'
import { TokenDonut } from '../components/TokenDonut'
import type { PersonelDetail, PersonelProfile } from '../model'
import type { Personel } from '../types'

export function GenelBilgilerTab({
  personel,
  profile,
  detail,
  manager,
}: {
  personel: Personel
  profile: PersonelProfile
  detail: PersonelDetail
  manager?: Pick<Personel, 'id' | 'pozisyon'>
}) {
  const { t } = useTranslation()
  const il = profile.il_adi
  const ilce = profile.ilce_adi
  const uni = profile.universite_id
  const opt = (group: string, v: string) => t(`hr.personel.form.options.${group}.${v}`)

  return (
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      <div className="space-y-4">
        <SectionCard title={t('hr.personel.genel.kisisel')} icon={<UserRound />}>
          <KvRow label={t('hr.personel.fields.ad')} value={profile.ad} />
          <KvRow label={t('hr.personel.fields.tckn')} value={profile.tckn} />
          <KvRow label={t('hr.personel.fields.dogum')} value={fmtDate(profile.dogum)} />
          <KvRow label={t('hr.personel.fields.cinsiyet')} value={opt('cinsiyet', profile.cinsiyet)} />
          <KvRow label={t('hr.personel.fields.medeni')} value={opt('medeni', profile.medeni)} />
          <KvRow label={t('hr.personel.fields.uyruk')} value={profile.uyruk} />
        </SectionCard>
        <SectionCard title={t('hr.personel.genel.organizasyon')} icon={<Building2 />}>
          <KvRow label={t('hr.personel.fields.departman')} value={profile.departman} />
          <KvRow label={t('hr.personel.fields.pozisyon')} value={profile.pozisyon} />
          <KvRow
            label={t('hr.personel.fields.yonetici')}
            value={
              manager ? (
                <Link
                  to={`/hr/personel/${manager.id}`}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  aria-label={t('hr.personel.detail.yoneticiProfil')}
                >
                  {personel.yonetici}
                  <ExternalLink className="size-3.5" />
                </Link>
              ) : (
                personel.yonetici
              )
            }
          />
          <KvRow label={t('hr.personel.fields.sube')} value={profile.sube} />
          <KvRow label={t('hr.personel.fields.istihdam')} value={opt('istihdam', profile.istihdam)} />
          <KvRow label={t('hr.personel.fields.calisma')} value={t(`hr.personel.calisma.${profile.calisma_turu}`)} />
          <KvRow label={t('hr.personel.fields.sigorta')} value={t(`hr.personel.form.options.sigorta.${profile.sigorta}`)} />
          {profile.sigorta === 'emekli' || profile.emeklilik_tarihi ? (
            <KvRow
              label={t('hr.personel.form.emeklilikTarihi')}
              value={[profile.emeklilik_tarihi ? fmtDate(profile.emeklilik_tarihi) : '—', profile.emeklilik_notu].filter(Boolean).join(' · ')}
            />
          ) : null}
          {profile.ortak ? (
            <KvRow
              label={t('hr.personel.form.ortak')}
              value={profile.ortaklik_payi ? t('hr.personel.form.ortakShare', { share: profile.ortaklik_payi }) : t('hr.personel.tags.ortak')}
            />
          ) : null}
          <KvRow label={t('hr.personel.form.workStart')} value={`${profile.calisma_baslangic} – ${profile.calisma_bitis}`} />
          <KvRow label={t('hr.personel.fields.kidem')} value={profile.kidem} />
          <KvRow label={t('hr.personel.form.brutMaas')} value={fmtMoney(profile.brut_maas)} />
        </SectionCard>
      </div>

      <div className="space-y-4">
        <SectionCard title={t('hr.personel.genel.iletisim')} icon={<PhoneCall />}>
          <KvRow label={t('hr.personel.fields.email')} value={profile.email} />
          <KvRow label={t('hr.personel.fields.telefon')} value={profile.telefon} />
          <KvRow label={t('hr.personel.fields.isTel')} value={profile.is_tel || '—'} />
          <KvRow label={`${t('hr.personel.form.il')} / ${t('hr.personel.form.ilce')}`} value={`${il} / ${ilce}`} />
          <KvRow label={t('hr.personel.fields.adres')} value={profile.adres} />
        </SectionCard>
        <SectionCard title={t('hr.personel.genel.acil')} icon={<ShieldAlert />}>
          <KvRow label={t('hr.personel.fields.ad')} value={profile.acil_ad || '—'} />
          <KvRow label={t('hr.personel.fields.yakinlik')} value={profile.acil_yakinlik || '—'} />
          <KvRow label={t('hr.personel.fields.telefon')} value={profile.acil_telefon || '—'} />
          <KvRow label={t('hr.personel.fields.email')} value={profile.acil_email || '—'} />
        </SectionCard>
        <SectionCard title={t('hr.personel.form.sections.egitim')} icon={<GraduationCap />}>
          <KvRow label={t('hr.personel.form.egitimDurumu')} value={opt('egitim', profile.egitim_durumu)} />
          <KvRow label={t('hr.personel.form.universite')} value={uni || '—'} />
          <KvRow label={t('hr.personel.form.mezunBolum')} value={profile.mezun_bolum || '—'} />
        </SectionCard>
      </div>

      <div className="space-y-4 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 xl:col-span-1 xl:block xl:space-y-4">
        <SectionCard title={t('hr.personel.detail.gecmis')} icon={<History />}>
          <ol className="space-y-0">
            {detail.gecmis.map((g, i) => (
              <li key={`${g.t}-${g.b}`} className="relative flex gap-3 pb-4 last:pb-0">
                {i < detail.gecmis.length - 1 ? (
                  <span className="absolute top-4 left-[6px] h-[calc(100%-8px)] w-px bg-border" aria-hidden />
                ) : null}
                <span
                  className={cn(
                    'relative z-10 mt-1 size-3.5 shrink-0 rounded-full border-2 bg-card',
                    g.old ? 'border-muted-foreground/40' : 'border-primary',
                  )}
                  aria-hidden
                />
                <div className="grid min-w-0 flex-1 gap-0.5 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:gap-3">
                  <span className="text-xs tabular-nums text-muted-foreground">{g.t}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{g.b}</div>
                    <div className="text-xs text-muted-foreground">{g.a}</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </SectionCard>

        <SectionCard title={t('hr.personel.detail.profilTamam')} icon={<PieChart />}>
          <div className="flex items-center gap-4">
            <TokenDonut pct={detail.profil.pct} size={96} />
            <p className="min-w-0 flex-1 text-xs text-muted-foreground">
              {t('hr.personel.detail.profilHintPct', { pct: detail.profil.pct })}
            </p>
          </div>
          <ul className="mt-4 space-y-2">
            {detail.profil.checklist.map((c) => (
              <li key={c.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  {c.done ? (
                    <CheckCircle2 className="size-4 shrink-0 text-success" />
                  ) : (
                    <Circle className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className={cn('truncate', !c.done && 'text-muted-foreground')}>
                    {t(`hr.personel.genel.check.${c.key}`)}
                  </span>
                </span>
                <span className={cn('shrink-0 text-xs font-medium', c.done ? 'text-success' : 'text-destructive')}>
                  {t(c.done ? 'hr.personel.detail.tamamlandi' : 'hr.personel.detail.eksik')}
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title={t('hr.personel.form.sections.panel')} icon={<KeyRound />}>
          <KvRow
            label={t('hr.personel.form.panelAccess')}
            value={profile.panel_erisim ? t('hr.personel.durum.aktif') : t('common.empty')}
          />
          <KvRow label={t('hr.personel.form.firmaEmail')} value={profile.firma_email || '—'} />
        </SectionCard>
      </div>
    </div>
  )
}
